import { useState } from "react";

interface FAQItem {
  question: string;
  answer: string;
}

interface CourseFAQProps {
  extraFaqs?: FAQItem[];
}

const baseFaqs: FAQItem[] = [
  {
    question: "Como vou acessar o curso após a compra?",
    answer:
      "Assim que o pagamento for confirmado, você receberá um e-mail com seu login e senha para acessar a plataforma. O acesso é imediato — disponível 24h por dia, 7 dias por semana, pelo computador, celular ou tablet.",
  },
  {
    question: "Por quanto tempo terei acesso ao conteúdo?",
    answer:
      "Você terá acesso por 12 meses a partir da data de compra. Durante esse período pode assistir quantas vezes quiser, no seu ritmo, sem pressa.",
  },
  {
    question: "O curso tem certificado?",
    answer:
      "Sim! Ao concluir todos os módulos você recebe um certificado digital assinado pela Gleice Lyra e pela Clientizah, que pode ser compartilhado no LinkedIn e usado no seu currículo.",
  },
  {
    question: "Posso parcelar o pagamento?",
    answer:
      "Sim. Aceitamos parcelamento em até 10x sem juros no cartão de crédito. Também é possível pagar via Pix ou boleto bancário à vista.",
  },
  {
    question: "E se eu não gostar? Tem garantia?",
    answer:
      "Oferecemos garantia incondicional de 7 dias. Se por qualquer motivo você não ficar satisfeita, basta entrar em contato dentro desse prazo e devolvemos 100% do seu dinheiro — sem burocracia, sem perguntas.",
  },
  {
    question: "Preciso ter experiência em vendas para fazer o curso?",
    answer:
      "Não! Os cursos foram desenvolvidos para todos os níveis. Se você está começando agora ou já tem anos de experiência, o Método C.A.T vai te levar para o próximo nível com conteúdo prático e aplicável imediatamente.",
  },
  {
    question: "As aulas são ao vivo ou gravadas?",
    answer:
      "As aulas são 100% gravadas, então você assiste quando e onde quiser. Não precisa se preocupar com horário fixo — o aprendizado acontece no seu tempo.",
  },
  {
    question: "Posso tirar dúvidas com a Gleice?",
    answer:
      "Sim! Além do conteúdo em vídeo, você terá acesso a um grupo exclusivo de alunas no WhatsApp onde a equipe da Clientizah responde dúvidas e a própria Gleice participa ativamente.",
  },
];

export default function CourseFAQ({ extraFaqs = [] }: CourseFAQProps) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const faqs = [...baseFaqs, ...extraFaqs];

  const toggle = (idx: number) => setOpenIndex(openIndex === idx ? null : idx);

  return (
    <section className="py-20 px-6 md:px-16 bg-white">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-14">
          <p className="text-xs tracking-[0.3em] uppercase text-[#b8962e] font-medium mb-3">Dúvidas</p>
          <h2 className="text-3xl md:text-4xl font-light text-black tracking-tight mb-4">
            Perguntas frequentes
          </h2>
          <p className="text-gray-500 text-sm leading-relaxed max-w-md mx-auto">
            Reunimos as principais dúvidas de quem está considerando se inscrever. Não encontrou a sua?{" "}
            <a href="mailto:contato@clientizah.com.br" className="text-[#b8962e] hover:underline">
              Fale conosco.
            </a>
          </p>
        </div>

        {/* FAQ list */}
        <div className="divide-y divide-gray-100">
          {faqs.map((faq, idx) => (
            <div key={idx} className="py-1">
              <button
                onClick={() => toggle(idx)}
                className="w-full flex items-center justify-between py-5 text-left gap-6 cursor-pointer group"
              >
                <span className={`text-sm font-medium tracking-wide transition-colors duration-200 ${openIndex === idx ? "text-[#b8962e]" : "text-black group-hover:text-[#b8962e]"}`}>
                  {faq.question}
                </span>
                <span className={`w-7 h-7 flex items-center justify-center shrink-0 border transition-all duration-300 ${openIndex === idx ? "border-[#b8962e] bg-[#b8962e] text-white rotate-45" : "border-gray-200 text-gray-400 group-hover:border-[#b8962e] group-hover:text-[#b8962e]"}`}>
                  <i className="ri-add-line text-sm"></i>
                </span>
              </button>

              <div
                className={`overflow-hidden transition-all duration-300 ease-in-out ${openIndex === idx ? "max-h-64 pb-5" : "max-h-0"}`}
              >
                <p className="text-gray-500 text-sm leading-relaxed pr-12">
                  {faq.answer}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* CTA bottom */}
        <div className="mt-14 bg-[#f9f8f6] border border-gray-100 p-8 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <p className="text-black text-sm font-semibold mb-1">Ainda tem dúvidas?</p>
            <p className="text-gray-500 text-xs leading-relaxed">Nossa equipe está pronta para te atender pelo WhatsApp.</p>
          </div>
          <a
            href="https://wa.me/5511981969885"
            target="_blank"
            rel="nofollow noopener noreferrer"
            className="whitespace-nowrap flex items-center gap-2 bg-black text-white text-xs tracking-widest uppercase px-7 py-3 hover:bg-[#b8962e] transition-all duration-300 cursor-pointer font-medium"
          >
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-whatsapp-line"></i>
            </span>
            Falar no WhatsApp
          </a>
        </div>
      </div>
    </section>
  );
}
